import { Field } from '@sitecore-jss/sitecore-jss-nextjs';
import type { ComponentProps } from 'lib/component-props';
import classNames from 'classnames/bind';
import { useState } from 'react';

import Button, { ButtonVariant } from 'components/helpers/Button/Button';
import styles from './Geocode.module.scss';
import TextInput from 'components/helpers/TextInput/TextInput';

import withValidDatasourceCheck from 'lib/enhancers/withValidDatasourceCheck';
import compose from 'lib/enhancers/compose';
import withStyles from 'lib/enhancers/withStyles';
import { dataFetcherGet } from 'lib/data-fetcher';
import { GeocodeResponse } from './Types/Geocode.Type';
import DistanceFilter from './DistanceFilter';
import AutoComplete from './AutoComplete';

export type GeocodeProps = ComponentProps & {
  fields: {
    showDistanceCalculations: Field<boolean>;
  };
};

const Geocode = (props: GeocodeProps): React.JSX.Element => {
  const cx = classNames.bind(styles);
  const { className, fields } = props;

  const [userAddress, setUserAddress] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [coordinates, setCoordinates] = useState<{ lat: number; lng: number; address: string }[]>([]);

  const isValidAddress = (address: string) => {
    const givenAddress = address.trim();
    const zipCodeRegex = /^\d{5}(-\d{4})?$/;
    if (zipCodeRegex.test(givenAddress)) return true;

    // Basic validation for city/state or full address (can be improved with more complex regex)
    const cityStateRegex = /^[a-zA-Z\s]+,\s*[A-Z]{2}$/;
    if (cityStateRegex.test(givenAddress)) return true;

    // Address line (must contain a number + some text)
    const hasNumber = /\d/;
    if (hasNumber.test(givenAddress) && givenAddress.length > 5) return true;

    return false;
  };

  const handleAddressSearch = async () => {
    setError('');
    setCoordinates([]);

    if (!userAddress.trim()) {
      setError('Please enter an address.');
      return;
    }
    if (!isValidAddress(userAddress)) {
      setError('Please enter a valid zip code.');
      return;
    }
    try {
      setLoading(true);

      const geocodeApiUrl = `/api/maps/geocodeByAzureMapService?query=${encodeURIComponent(userAddress.trim())}`;
      const response = await dataFetcherGet<GeocodeResponse>(geocodeApiUrl);

      const results = response?.data?.geocodeData?.results;
      if (results?.length > 0) {
        // Normalize address comparison
        const normalizedAddresses = results.map((result) => {
          const address = result.properties?.address;

          return `${address?.streetNumber || ''} ${address?.streetName || ''} ${address?.locality || ''} ${
            address?.adminDistricts?.[0]?.shortName || ''
          } ${address?.postalCode || ''}`
            .replace(/\s+/g, '')
            .trim()
            .toLowerCase();
        });

        const allSameAddress = new Set(normalizedAddresses).size === 1;

        let finalResults = results;

        // Only prefer Rooftop when all addresses are effectively the same
        if (allSameAddress) {
          const rooftopResults = results.filter((result) =>
            result.properties?.geocodePoints?.some((point) => point.calculationMethod === 'Rooftop')
          );

          if (rooftopResults.length > 0) {
            finalResults = rooftopResults;
          }
        }
        const mappedResults = finalResults.map((result) => {
          const [lng, lat] = result.geometry?.coordinates || [];
          return {
            lat,
            lng,
            address: result.properties?.address?.formattedAddress || 'Unknown address',
          };
        });
        setCoordinates(mappedResults);
      } else {
        setCoordinates([]);
        setError('No results found for the provided address.');
      }
    } catch (error) {
      console.error('Error fetching location:', error);
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={cx('geocode', 'component-container-edge', className)}>
      <div className={cx('address-search')}>
        <h2 className={cx('header')}>Geocode</h2>
        <label htmlFor="userAddress" className={cx('address-label')}>
          Enter Address
        </label>

        <TextInput
          value={userAddress}
          type="text"
          id="userAddress"
          onChange={(e) => setUserAddress(e.target.value)}
          placeholder="Zip or City/State or full address"
          className={cx('inputBox')}
        />

        <Button variant={ButtonVariant.Primary} onClick={handleAddressSearch} disabled={loading}>
          {loading ? 'Searching...' : 'Search'}
        </Button>

        {error && <p className={cx('error-msg')}>{error}</p>}

        {coordinates.map((item, index) => (
          <div className={cx('coordinates')} key={index}>
            <p>
              <span className={cx('coordinate-label')}>Latitude:</span> {item.lat}
            </p>
            <p>
              <span className={cx('coordinate-label')}>Longitude:</span> {item.lng}
            </p>
            <p>
              <span className={cx('coordinate-label')}>Address:</span> {item.address}
            </p>
          </div>
        ))}
      </div>
      <AutoComplete headerTitle="Auto Complete Search" />
      {fields?.showDistanceCalculations?.value && <DistanceFilter headerTitle="Sample Event Distance Filter" />}
    </div>
  );
};

export default compose<GeocodeProps>(withValidDatasourceCheck(), withStyles())(Geocode);
